import {
    createCartItem,
    deleteCartItem,
    replaceCartItem,
    updateCart,
    updateCartEventCredit,
    updateCartItem,
} from '@general/api/cartAPI';
import { fetchEventByCode, fetchRegistrantClass } from '@general/api/eventAPI';
import { fetchMember, fetchMemberDuesEligibility } from '@general/api/memberAPI';
import { getMemberFromCache } from '@general/cache/memberCache';
import { checkIfMembershipWillLapse } from '@general/helpers/event.helpers';
import productTypes from '@general/productTypes';
import reactQueryClient from '@general/reactQueryClient';
import { calculateAppliedStoreCredit, getCartCalculatedValues } from '@general/transforms/cart.transform';

export const membershipYears = {
    ONE: 1,
    THREE: 3,
    FIVE: 5,
    TEN: 10,
};

export const addEventRegistrationToCart = async ({ cart, event, memberId, viewerId, useSpeedBump = false }) => {
    const memberData = await getMemberFromCache(memberId);
    if (!memberData) {
        return;
    }

    if (getDoesCartHaveEventRegistrations({ cart, event, memberId })) {
        return;
    }

    if (useSpeedBump && checkIfMembershipWillLapse({ member: memberData, event, cart })) {
        // Membership is only USD, can't add membership to cart with non-USD event
        if (event.locale === 'US') {
            await addMembershipToCart({ cart, memberId, viewerId, years: membershipYears.THREE });
        }
    }

    const {
        data: { data: registrantClasses },
    } = await fetchRegistrantClass({
        memberId,
        cartId: cart.id,
        eventId: event.id,
    });

    const fullProgram = event.functions.find(func => func.code === 'FP');
    const cartItemData = [
        createEventLineItemPayload({
            event,
            member: memberData,
            promoCode: cart._calculated?.discounts[0]?.promotional_code || '',
            registrantClasses,
            session: fullProgram,
        }),
    ];

    return createCartItem({
        cartId: cart.id,
        cartItemData,
        viewerId,
    });
};

export const addMembershipToCart = async ({ cart, memberId, viewerId, years = 3 }) => {
    const member = await fetchMember(memberId);
    if (!member) {
        return;
    }

    if (getDoesCartHaveMemberships({ cart, memberId })) {
        return;
    }

    const duesEligibilityResponse = await fetchMemberDuesEligibility(memberId);
    const duesEligibility = duesEligibilityResponse?.data?.data;
    if (!duesEligibility) {
        return;
    }

    const cartItemData = [
        createMembershipLineItemPayload({
            member,
            duesEligibility,
            years,
        }),
    ];

    return createCartItem({
        cartId: cart.id,
        cartItemData,
        viewerId,
    });
};

export const updateMembershipInCart = async ({ cart, memberId, viewerId, years = 3 }) => {
    const member = await fetchMember(memberId);

    if (!member) {
        return;
    }

    const membershipCartItem = cart.items.find(
        item => item.product_type === productTypes.MEMBERSHIP && item.member_id === memberId
    );

    if (!membershipCartItem) {
        return;
    }

    const duesEligibilityResponse = await fetchMemberDuesEligibility(memberId);
    const duesEligibility = duesEligibilityResponse?.data?.data;

    if (!duesEligibility) {
        return;
    }

    const updatedCartItemData = updateMembershipLineItemPayload({
        duesEligibility,
        member,
        years,
        membershipCartItem,
    });

    return replaceCartItem({
        cartId: cart.id,
        cartItemId: membershipCartItem.id,
        cartItemData: updatedCartItemData,
        viewerId,
    });
};

export const updateMembershipLineItemPayload = ({ duesEligibility, member, years, membershipCartItem }) => {
    const currentDues = duesEligibility.dues.find(dues => dues.years === years);

    const updatedCartItem = {
        ...membershipCartItem,
        name: currentDues.description,
        member_id: member.id,
        product_id: currentDues.id,
        product_type: currentDues.product_type,
        quantity: 1,
        price: currentDues.price,
        meta_info: {
            member_id: member.id,
            member_name: `${member.first_name} ${member.last_name}`,
            company_name: member.deferred_independent
                ? null
                : member.deferred_company?.name || member.company?.name || null,
            product_id: currentDues.id,
            product_code: currentDues.code,
            dues_description: currentDues.description,
            dues_years: currentDues.years,
        },
    };

    delete updatedCartItem.id;

    return updatedCartItem;
};

export const createMembershipLineItemPayload = ({ member, duesEligibility, years }) => {
    const dues = duesEligibility.dues.find(d => d.years === years);

    if (!dues) {
        return undefined;
    }

    return {
        name: dues.description,
        member_id: member.id,
        product_id: dues.id,
        product_type: dues.product_type,
        quantity: 1,
        price: dues.price,
        meta_info: {
            member_id: member.id,
            member_name: `${member.first_name} ${member.last_name}`,
            company_name: member.deferred_independent
                ? null
                : member.deferred_company?.name || member.company?.name || null,
            product_id: dues.id,
            product_code: dues.code,
            dues_description: dues.description,
            dues_years: dues.years,
        },
    };
};

/*
Explanation of registrant classes:

Base class is always either M or NM.
If a member’s effective company is a retailer, RM gets added.
Else if a member is staff, ST gets added.
Else if a member is in the public sector, PS gets added.
Else if a member is a student, SM gets added.

So there should only ever be two. It could be expressed something like
([M|NM], ?[RM|ST|PS|SM])
*/

export const createEventLineItemPayload = ({
    event,
    member,
    promoCode,
    registrantClasses = [],
    session,
    // isMembershipLapsed,
    // shouldRenewMembership,
}) => {
    let feeToUse = null;

    // If function has a student fee, check the member's registrant class
    const retailerFee = session.fees.find(fee => fee.registrant_class === 'RM');
    const studentFee = session.fees.find(fee => fee.registrant_class === 'SM');
    const isMemberRetailer = member.member_type === 'Retailer';
    const isMemberStudent = registrantClasses.includes('SM');
    const isMemberActive = !registrantClasses.includes('NM');

    // if (isMembershipLapsed) {
    //     if (shouldRenewMembership) {
    //         if (isMemberRetailer) {
    //             feeToUse = session.fees.find(fee => fee.registrant_class === 'RM');
    //         } else {
    //             feeToUse = session.fees.find(fee => fee.registrant_class === 'M');
    //         }
    //     } else {
    //         feeToUse = session.fees.find(fee => fee.registrant_class === 'NM');
    //     }
    // } else if (!isMemberActive) {
    //     feeToUse = session.fees.find(fee => fee.registrant_class === 'NM');
    // } else if (!!studentFee && isMemberStudent) {
    //     feeToUse = studentFee;
    // } else {
    //     registrantClass.forEach(memberRegistrantClass => {
    //         if (!feeToUse) {
    //             feeToUse = session.fees.find(fee => fee.registrant_class === memberRegistrantClass);
    //         }
    //     });
    // }

    // TODO: if member is active OR user is lapsed but membership is in the cart
    if (isMemberActive) {
        if (isMemberRetailer && retailerFee) {
            feeToUse = retailerFee;
        } else if (isMemberStudent && studentFee) {
            feeToUse = studentFee;
        } else {
            registrantClasses.forEach(registrantClass => {
                if (!feeToUse) {
                    feeToUse = session.fees.find(fee => fee.registrant_class === registrantClass);
                }
            });
        }
    }

    // Use NM fee by default
    if (!feeToUse) {
        feeToUse = session.fees.find(fee => fee.registrant_class === 'NM');
    }

    // function parseURLQueryString() {
    //     const queryString = window.location.href.split('?').length > 1 ? window.location.href.split('?')[1] : '';
    //     const queryParamArray = queryString.split('&');
    //     const queryParamObject = {};

    //     queryParamArray.forEach(queryParam => {
    //         const keyValuePair = queryParam.split('=').map(decodeURIComponent);
    //         queryParamObject[keyValuePair[0]] = keyValuePair[1];
    //     });

    //     return queryParamObject;
    // }

    // function parseUTMValuesFromQueryString() {
    //     const queryParams = parseURLQueryString();
    //     const utmValues = {};

    //     Object.keys(queryParams).forEach(key => {
    //         if (key.substring(0, 3).toUpperCase() === 'UTM') {
    //             utmValues[key] = queryParams[key];
    //         }
    //     });

    //     return utmValues;
    // }

    // const utmQueryParams = utility.parseUTMValuesFromQueryString();
    // if (JSON.stringify(utmQueryParams) !== '{}') {
    //     metaInfo.utm = utmQueryParams;
    // }

    return {
        member_id: member.id,
        meta_info: {
            company_name: member.deferred_independent
                ? null
                : member.deferred_company?.name || member.company?.name || null,
            event_code: event.code,
            event_fee_auto_enroll: feeToUse.auto_enroll,
            event_function_code: session.code,
            event_function_id: session.id,
            event_function_title: session.title,
            event_id: event.id,
            event_locale: event.locale,
            event_name: event.title,
            event_registrant_class: feeToUse.registrant_class,
            member_id: member.id,
            member_name: `${member.first_name} ${member.last_name}`,
            member_first_name: member.first_name,
            member_last_name: member.last_name,
            product_id: feeToUse.id,
            promo_code: promoCode,
            begins_at: event.begins_at,
            ends_at: event.ends_at,
        },
        price: feeToUse.price,
        product_id: feeToUse.id,
    };
};

export const getDoesCartHaveEventRegistrations = ({ cart, event, memberId }) => {
    let filteredItems = cart.items.filter(item => item.product_type === productTypes.EVENT);

    if (event) {
        filteredItems = filteredItems.filter(item => item.meta_info?.event_code === event.code);
    }

    if (memberId) {
        return filteredItems.some(item => item.member_id === memberId);
    }

    return filteredItems.length > 0;
};

export const getDoesCartHaveMemberships = ({ cart, memberId }) => {
    const membershipItems = cart.items.filter(item => item.product_type === productTypes.MEMBERSHIP);

    if (memberId) {
        return membershipItems.some(item => item.member_id === memberId);
    }

    return membershipItems.length > 0;
};

export const processCartErrors = async ({ cart, viewer }) => {
    if (!cart.member_id === null) {
        await updateCart({
            cartId: cart.id,
            cartData: {
                member_id: viewer.id,
            },
            viewerId: viewer.id,
        });
    }

    if (!cart.errors.items) {
        return cart;
    }

    let cartResponse = await new Promise(resolve => {
        (async () => {
            Object.keys(cart.errors.items).forEach(async lineItemId => {
                const cartItem = cart.items.find(item => item.id === lineItemId);
                const errorMessages = cart.errors.items[lineItemId];

                if (errorMessages.find(message => message.includes('Invalid fee'))) {
                    cartResponse = await replaceEventFee({ cart, cartItem, viewer });
                    resolve(cartResponse);
                } else if (errorMessages.find(message => message.includes('Member is not eligible'))) {
                    cartResponse = await removeMembership({ cart, cartItem, viewer });
                    resolve(cartResponse);
                } else if (
                    errorMessages.find(
                        message => message.includes('Promo code') && message.includes('is no longer valid')
                    )
                ) {
                    cartResponse = await updateCartItem({
                        cartId: cart.id,
                        cartItemId: lineItemId,
                        cartItemData: {
                            meta_info: {
                                ...cartItem.meta_info,
                                promo_code: null,
                            },
                        },
                        viewerId: viewer.recordId,
                    });

                    resolve(cartResponse);
                }
            });
        })();
    });

    if (cartResponse) {
        return processCartErrors({ cart: cartResponse, viewer });
    }

    return cart;
};

export const removeMembership = async ({ cart, cartItem, viewer }) => {
    const membershipItems = cart.items.filter(
        item =>
            item.member_id === cartItem.member_id &&
            [productTypes.MEMBERSHIP, productTypes.PAC, productTypes.SIC, productTypes.FOUNDATION_DONATION].includes(
                item.product_type
            )
    );

    return membershipItems.reduce(async (promise, item) => {
        await promise;
        return deleteCartItem({ cartId: cart.id, cartItemId: item.id, viewerId: viewer.recordId });
    }, Promise.resolve());
};

export const removeOrphanedDonations = async ({ cart, viewer }) => {
    const donationItems = cart.items.filter(item =>
        [productTypes.SIC, productTypes.PAC, productTypes.FOUNDATION_DONATION].includes(item.product_type)
    );

    let cartResponse;
    return donationItems.reduce(async (previousPromise, donationItem) => {
        await previousPromise;
        const duesItem = cart.items.find(
            item => item.member_id === donationItem.member_id && item.product_type === 'dues'
        );

        if (!duesItem) {
            cartResponse = await deleteCartItem({
                cartId: cart.id,
                cartItemId: donationItem.id,
                viewerId: viewer.recordId,
            });
        }

        return Promise.resolve(cartResponse);
    }, Promise.resolve(cart));
};

export const replaceEventFee = async ({ cart, cartItem, viewer }) => {
    const [event, member, registrantClassResponse] = await Promise.all([
        fetchEventByCode(cartItem.meta_info.event_code),
        fetchMember(cartItem.member_id),
        fetchRegistrantClass({ memberId: cartItem.member_id, cartId: cart.id, eventId: cartItem.meta_info.event_id }),
    ]);

    const session = event.functions.find(func => func.id === cartItem.meta_info.event_function_id);
    const registrantClasses = registrantClassResponse.data?.data;
    const cartItemData = createEventLineItemPayload({
        event,
        session,
        member,
        registrantClasses,
    });

    return replaceCartItem({
        cartId: cart.id,
        cartItemId: cartItem.id,
        cartItemData,
        viewerId: viewer.recordId,
    });
};

export const resetFlashNotices = async ({ cart, viewer }) => {
    return cart.items.reduce(async (previousPromise, item) => {
        await previousPromise;

        if (item.meta_info.flash_notice) {
            const { flash_notice: _, ...metaInfo } = item.meta_info;
            return updateCartItem({
                cartId: cart.id,
                cartItemId: item.id,
                cartItemData: { ...item, ...{ meta_info: metaInfo } },
                viewerId: viewer.recordId,
            });
        }
    }, Promise.resolve());
};

export const applyEventCredit = async ({ cart, viewer }) => {
    try {
        const updatedCart = await updateCartEventCredit({
            cartId: cart.id,
            viewerId: viewer.recordId,
            shouldApplyCredit: true,
        });

        reactQueryClient.setQueryData(`cart-${viewer.recordId}`, existingCartData => {
            if (!existingCartData) {
                return null;
            }

            const newCartData = { ...existingCartData };

            newCartData.available_event_credit = updatedCart.available_event_credit;
            newCartData.event_credit = updatedCart.event_credit;
            newCartData.items = updatedCart.items;
            const updatedCalculatedValues = getCartCalculatedValues(updatedCart);
            newCartData._calculated = updatedCalculatedValues;

            return newCartData;
        });
    } catch (error) {
        alert('Error applying event credit.');
    }
};

export const removeEventCredit = async ({ cart, viewer }) => {
    try {
        const updatedCart = await updateCartEventCredit({
            cartId: cart.id,
            viewerId: viewer.recordId,
            shouldApplyCredit: false,
        });

        reactQueryClient.setQueryData(`cart-${viewer.recordId}`, existingCartData => {
            if (!existingCartData) {
                return null;
            }

            const newCartData = { ...existingCartData };
            newCartData.available_event_credit = updatedCart.available_event_credit;
            newCartData.event_credit = updatedCart.event_credit;
            newCartData.items = updatedCart.items;
            const updatedCalculatedValues = getCartCalculatedValues(updatedCart);
            newCartData._calculated = updatedCalculatedValues;

            return newCartData;
        });
    } catch (error) {
        alert('Error removing event credit.');
    }
};
